import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import { useMemo, useState, type CSSProperties, type MouseEvent, type ReactNode } from "react";
import { cx } from "../util";
import { Skeleton } from "./Spinner";

export type SortDir = "asc" | "desc";

export interface SortState {
  key: string;
  dir: SortDir;
}

export interface Column<T> {
  key: string;
  header: ReactNode;
  cell: (row: T, index: number) => ReactNode;
  /**
   * Enables client-side sorting on this column (uncontrolled tables).
   * For server-side sorting set `sortable` and pass `sort` + `onSortChange`.
   */
  sortValue?: (row: T) => string | number | null | undefined;
  sortable?: boolean;
  width?: number | string;
  align?: "left" | "right" | "center";
  className?: string;
}

export interface TableProps<T> {
  columns: ReadonlyArray<Column<T>>;
  rows: ReadonlyArray<T> | undefined;
  rowKey: (row: T) => string;
  /** Shows skeleton rows (keeps the header). */
  loading?: boolean;
  skeletonRows?: number;
  /** Rendered in place of rows when there are none (use <EmptyState size="sm">). */
  empty?: ReactNode;
  /** Controlled sort. */
  sort?: SortState | null;
  onSortChange?: (sort: SortState) => void;
  /** Initial sort for uncontrolled (client-side) sorting. */
  defaultSort?: SortState;
  /** Whole-row click. Clicks on buttons/links/inputs inside the row are ignored. */
  onRowClick?: (row: T) => void;
  rowClassName?: (row: T) => string | undefined;
  /** Screen-reader caption. */
  caption?: string;
  /** Under the table (pagination, "Load more"). */
  footer?: ReactNode;
  density?: "compact" | "comfortable";
  className?: string;
}

function compare(a: unknown, b: unknown): number {
  if (a === b) return 0;
  if (a === null || a === undefined) return 1;
  if (b === null || b === undefined) return -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b), undefined, { sensitivity: "base", numeric: true });
}

export function Table<T>({
  columns,
  rows,
  rowKey,
  loading,
  skeletonRows = 6,
  empty,
  sort,
  onSortChange,
  defaultSort,
  onRowClick,
  rowClassName,
  caption,
  footer,
  density = "comfortable",
  className,
}: TableProps<T>) {
  const controlled = sort !== undefined;
  const [localSort, setLocalSort] = useState<SortState | null>(defaultSort ?? null);
  const active = controlled ? sort : localSort;

  const sorted = useMemo(() => {
    if (!rows) return [];
    if (controlled || !active) return rows;
    const col = columns.find((c) => c.key === active.key);
    if (!col?.sortValue) return rows;
    const get = col.sortValue;
    const out = [...rows].sort((x, y) => compare(get(x), get(y)));
    return active.dir === "desc" ? out.reverse() : out;
  }, [rows, columns, active, controlled]);

  function toggle(key: string) {
    const next: SortState = active?.key === key ? { key, dir: active.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" };
    if (onSortChange) onSortChange(next);
    if (!controlled) setLocalSort(next);
  }

  function rowClick(e: MouseEvent<HTMLTableRowElement>, row: T) {
    if (!onRowClick) return;
    const target = e.target as HTMLElement;
    // Events from portals (menus, dialogs opened from this row) bubble through React too.
    if (!e.currentTarget.contains(target)) return;
    if (target.closest("button, a, input, select, textarea, label, [role='menuitem'], [data-row-ignore]")) return;
    onRowClick(row);
  }

  const showEmpty = !loading && sorted.length === 0;

  return (
    <div className={cx("gf-table-wrap", className)}>
      <div className="gf-table-scroll">
        <table className={cx("gf-table", `gf-table--${density}`, onRowClick && "gf-table--clickable")}>
          {caption ? <caption className="gf-sr-only">{caption}</caption> : null}
          <thead>
            <tr>
              {columns.map((c) => {
                const canSort = !!c.sortValue || !!c.sortable;
                const isActive = active?.key === c.key;
                const style: CSSProperties = { width: c.width, textAlign: c.align ?? "left" };
                return (
                  <th
                    key={c.key}
                    scope="col"
                    style={style}
                    className={c.className}
                    aria-sort={isActive ? (active!.dir === "asc" ? "ascending" : "descending") : canSort ? "none" : undefined}
                  >
                    {canSort ? (
                      <button type="button" className={cx("gf-table__sort", isActive && "gf-table__sort--active")} onClick={() => toggle(c.key)}>
                        <span>{c.header}</span>
                        {isActive ? (
                          active!.dir === "asc" ? (
                            <ArrowUp size={13} aria-hidden="true" />
                          ) : (
                            <ArrowDown size={13} aria-hidden="true" />
                          )
                        ) : (
                          <ArrowUpDown size={13} aria-hidden="true" className="gf-table__sorthint" />
                        )}
                      </button>
                    ) : (
                      c.header
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading
              ? Array.from({ length: skeletonRows }, (_, i) => (
                  <tr key={`sk-${i}`} className="gf-table__skeleton">
                    {columns.map((c, j) => (
                      <td key={c.key} style={{ textAlign: c.align ?? "left" }}>
                        <Skeleton height={12} width={j === 0 ? "70%" : `${40 + ((i * 7 + j * 13) % 40)}%`} />
                      </td>
                    ))}
                  </tr>
                ))
              : sorted.map((row, i) => (
                  <tr
                    key={rowKey(row)}
                    className={rowClassName?.(row)}
                    onClick={onRowClick ? (e) => rowClick(e, row) : undefined}
                    tabIndex={onRowClick ? 0 : undefined}
                    onKeyDown={
                      onRowClick
                        ? (e) => {
                            if ((e.key === "Enter" || e.key === " ") && e.target === e.currentTarget) {
                              e.preventDefault();
                              onRowClick(row);
                            }
                          }
                        : undefined
                    }
                  >
                    {columns.map((c) => (
                      <td key={c.key} style={{ textAlign: c.align ?? "left" }} className={c.className}>
                        {c.cell(row, i)}
                      </td>
                    ))}
                  </tr>
                ))}
          </tbody>
        </table>
        {showEmpty ? <div className="gf-table__empty">{empty ?? <p className="gf-table__emptytext">Nothing here yet.</p>}</div> : null}
      </div>
      {footer ? <div className="gf-table__foot">{footer}</div> : null}
    </div>
  );
}
